import {
  METRICS_CUSTOM_COLUMN,
  METRICS_CUSTOM_EVENT,
  METRICS_CUSTOM_EVENT_HIDDEN_REVENUE,
  METRICS_CUSTOM_EVENT_REVENUE,
} from '@/services/constants/report';

export type MetricHelpContext = {
  translate: (key: string) => string;
  customEvents: Record<string, { slug: string; name: string }>;
  getCustomColumns: () => Record<string, { formula?: string; description?: string }>;
  metricsHelp: Record<string, { short?: string; full?: string }>;
  reportMetricsName: (value: string) => string;
};

export const resolveMetricsTextByValue = (value: string, context: MetricHelpContext) => {
  const text = context.reportMetricsName(value);

  if (text) {
    return text;
  }

  const event = Object.values(context.customEvents).find((item) => {
    return `${METRICS_CUSTOM_EVENT_HIDDEN_REVENUE}${item.slug}` === value
      || `${METRICS_CUSTOM_EVENT}${item.slug}` === value
      || `${METRICS_CUSTOM_EVENT_REVENUE}${item.slug}` === value;
  });

  if (event) {
    return event.name;
  }

  return '';
};

const resolveCustomColumnFormulaHelp = (
  name: string,
  context: MetricHelpContext,
  useDescription: boolean,
) => {
  const column = context.getCustomColumns()[name.slice(METRICS_CUSTOM_COLUMN.length)] || {};
  const { description, formula = '' } = column;

  if (useDescription && description) {
    return description;
  }

  return formula.replace(/{(.+?)}/g, (_, value) => resolveMetricsTextByValue(value, context));
};

export const resolveShortMetricHelp = (name: string, context: MetricHelpContext) => {
  if (name.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || name.startsWith(METRICS_CUSTOM_EVENT)) {
    return context.translate('Conversion event value specified with “cet“ parameter');
  }

  if (name.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
    return context.translate('Revenue of custom event that was not sent to the traffic source');
  }

  if (name.startsWith(METRICS_CUSTOM_COLUMN)) {
    return resolveCustomColumnFormulaHelp(name, context, false);
  }

  return context.metricsHelp[name]?.short || null;
};

export const resolveFullMetricHelp = (name: string, context: MetricHelpContext) => {
  if (name.startsWith(METRICS_CUSTOM_EVENT_REVENUE) || name.startsWith(METRICS_CUSTOM_EVENT)) {
    return context.translate('Conversion event value specified with “cet“ parameter. Allows to track additional income from upsells.');
  }

  if (name.startsWith(METRICS_CUSTOM_EVENT_HIDDEN_REVENUE)) {
    return context.translate('Revenue of custom event that was not sent to the traffic source.');
  }

  if (name.startsWith(METRICS_CUSTOM_COLUMN)) {
    return resolveCustomColumnFormulaHelp(name, context, true);
  }

  return context.metricsHelp[name]?.full || null;
};
